import React from 'react';
import {
  GuidePage, Section, Steps, QuestionList, WhatHappensNext, Trouble, Footnote, Para, Bullets, Callout,
  startCaseStep, subjectRow, troubleSleeveRows, TROUBLE_CATEGORY_MISSING, TROUBLE_WRONG_HOUSEHOLD,
} from '../GuideKit';

/* Trading → Place Trade Order → Buy Order / Sell Order / Buy / Sell Order.
 *
 * Written against the live flow Prosperity_Workflow_Create_New_Case_Trading
 * (version 1, activated 2026-10-04): screens "Place Trade Order" (risk
 * tolerance / IPS questions and the registration table), then "Buy Order",
 * "Sell Order" or "Buy / Sell Order". The three sub-categories ask the same
 * questions on the same layout; only the second screen's title and its sleeve
 * table label differ. A linked Compliance case (Paperwork Review → Review IPS
 * / IPS Addendum) is opened when the risk tolerance is changing or there is no
 * updated IPS. Shared chrome and policy copy live in GuideKit.js.
 */

const STEPS = [
  startCaseStep({
    recordType: 'Trading',
    category: 'Place Trade Order',
    subCategory: 'Buy Order, Sell Order or Buy / Sell Order',
    subject: subjectRow('Buy order - 0989-022356'),
    lookups: [['Household', 'The client household. It decides which accounts you can pick later, and the advisor and office are taken from it.']],
    note: (
      <>
        <strong>All three sub-categories ask exactly the same questions.</strong> The choice sets the title of
        the second screen and is recorded on the case so Trading knows the intent.
      </>
    ),
  }),
  {
    title: 'Answer the risk tolerance questions and pick the account',
    body: (
      <>
        <p style={{ marginBottom: '0.5rem' }}>
          The <strong>Place Trade Order</strong> screen opens with the risk tolerance question. Depending on your
          answer it may ask about an updated IPS - see the reference below.
        </p>
        <p style={{ margin: 0 }}>
          The <strong>Place Trade Order Accounts</strong> table then appears. Pick one row - it lists the
          household&rsquo;s active registrations (the account as a whole, not its sleeves), largest Total Value
          first, with columns Account Number, Account Details, Total Cash and Total Value. There is a search bar
          above it. Click Next.
        </p>
      </>
    ),
  },
  {
    title: 'Pick the sleeve',
    body: (
      <p style={{ margin: 0 }}>
        The next screen is titled <strong>Buy Order</strong>, <strong>Sell Order</strong> or{' '}
        <strong>Buy / Sell Order</strong> to match your Sub-Category. Its table - <em>Select Sleeve (Place Buy
        Order)</em>, <em>Select Sleeve (Place Sell Order)</em> or <em>Select Sleeve (Place Buy/Sell Order)</em> -
        lists the sleeves under the account you just picked, with Account Number, Account Details, Model, Cash
        Balance and Balance. Pick one row; it is required. The case is linked to this sleeve.
      </p>
    ),
  },
  {
    title: 'Write the trade instructions and attach the Trade Template',
    body: (
      <>
        <p style={{ marginBottom: '0.5rem' }}>
          Below the table is a required box: <em>Please provide all relevant information for the Trading
          Team...</em> It becomes the case description Trading reads first, so put the full instruction here -
          what to buy or sell, tickers, and dollar amounts, share quantities or percentages.
        </p>
        <p style={{ margin: 0 }}>
          Once the box has something in it, a Trade Template notice and a file upload appear. The upload is
          required - attach your completed Trade Template (downloaded from the link in the notice). The files
          are attached to the case.
        </p>
      </>
    ),
  },
  {
    title: 'Acknowledge and submit',
    body: (
      <p style={{ margin: 0 }}>
        Read the Operations and Trading notice at the foot of the screen - it governs timing and what the teams
        can and cannot do. Select <em>I have read, understand, and agree to the above information</em> under
        Acknowledgement and click Next. FSC creates the case and opens it in a new browser tab. Note the case
        number for follow-up.
      </p>
    ),
  },
];

const QUESTIONS = [
  {
    screen: 'Place Trade Order',
    q: "Is the Risk Tolerance changing for the Household and / or are these Trades within the associated Clients' Risk Tolerance Range?",
    when: 'Always - this is the first question',
    options: [
      'Yes (The Risk Tolerance is Changing / Are outside the Clients Risk Tolerance Range)',
      'No (The Risk Tolerance is NOT Changing / Is Within the Clients Risk Tolerance Range)',
    ],
    note: 'Answering Yes also opens a linked Compliance case to review the IPS - see What happens next. Answering No takes you straight to the account table.',
  },
  {
    q: 'Do you have an updated IPS or IPS Addendum?',
    when: 'Only if the risk tolerance is changing or the trades are outside the range',
    options: [
      'Yes (I have a signed IPS/Updated IPS or IPS Addendum)',
      'No (I do not have an IPS/Updated IPS or IPS Addendum)',
    ],
  },
  {
    q: 'Why / When do you expect to have it?',
    when: 'Only if you do not have an updated IPS or IPS Addendum',
    note: 'An IPS notice appears with this question: an updated IPS or IPS Addendum is required when the household’s risk tolerance changes or trades fall outside it. "Please submit the updated IPS or Addendum within 30 days." If it is not received by then, the account is converted to a retail relationship.',
    warn: true,
  },
  {
    screen: 'Buy Order / Sell Order / Buy / Sell Order',
    q: 'Please provide all relevant information for the Trading Team...',
    when: 'Always, below the sleeve table',
    note: 'Required. Becomes the case description.',
  },
  {
    q: 'File upload (Trade Template)',
    when: 'Once the instructions box has something in it',
    note: (
      <>
        Required. The notice above it reads: &ldquo;You MUST use the spreadsheet in the link below for any trade
        related instructions. Failure to do so will result in a NIGO.&rdquo; It names the management styles this
        applies to especially - Advisor Directed Portfolios (ADP), Client Directed (CD) and Non‐Conforming (NC),
        Innovator - which need position tickers and percentages or dollar amounts.
      </>
    ),
    warn: true,
  },
];

const TROUBLE = [
  TROUBLE_CATEGORY_MISSING('Trading', 'Place Trade Order'),
  {
    h: "The account I expected isn't in the Place Trade Order Accounts table",
    p: [
      'That first table lists registrations, not sleeves, and only active ones under the household you chose. An inactive registration does not appear.',
    ],
  },
  troubleSleeveRows('The sleeve table only lists active sleeves with a balance over $50 under the account you picked. Cancelled sleeves ending in CNX and Distribution or Contribution sleeves are filtered out. If the sleeve table is empty, go Back and check you picked the right account.'),
  {
    h: "The upload and Trade Template notice haven't appeared",
    p: ['They stay hidden until the instructions box has something in it.'],
  },
  TROUBLE_WRONG_HOUSEHOLD,
];

export default function PlaceTradeOrder() {
  return (
    <GuidePage
      title="Place Trade Order Requests"
      icon="arrow-down-up"
      path="Trading → Place Trade Order → Buy Order, Sell Order or Buy / Sell Order"
      minutes={5}
      intro="How to submit a buy, sell or buy / sell order to the Prosperity Trading team - what to gather first, every question you will be asked, and what happens after you submit."
    >
      <Section title="Before you start" icon="clipboard-check">
        <Para>Have these ready. The form will not let you submit without the trade instructions and an uploaded file.</Para>
        <Bullets items={[
          <><strong>The account and the sleeve</strong> the trade is in. You pick them from lists, so you do not need account numbers memorised.</>,
          <><strong>A completed Trade Template</strong> - the spreadsheet linked on the order screen. The upload is required, and trade instructions not on the template are returned NIGO.</>,
          <><strong>The trade instructions</strong> - what to buy or sell, tickers, and amounts, quantities or percentages.</>,
          <><strong>The risk tolerance answer</strong> - whether the household&rsquo;s risk tolerance is changing or the trades fall outside it, and if so whether you have a signed, updated IPS or IPS Addendum.</>,
        ]} />
        <Callout tone="warn">
          Individual buy and sell orders on a Separately Managed Account (SMA) are rejected. To liquidate or
          reallocate an SMA, submit a separate ticket to Trading.
        </Callout>
      </Section>

      <Section title="Submitting the request" icon="list-ol">
        <Steps steps={STEPS} />
      </Section>

      <Section title="Every question, and when it appears" icon="question-circle">
        <Para>Use this to prepare before you sit down with a client, or to work out why a question you expected has not shown up.</Para>
        <QuestionList questions={QUESTIONS} />
      </Section>

      <WhatHappensNext
        team="trading"
        after={(
          <Callout>
            <strong>Linked Compliance case.</strong> If you said the risk tolerance is changing (or the trades are
            outside the range), or that you do not have an updated IPS, FSC also opens a Compliance case -
            Category <em>Paperwork Review</em>, Sub-Category <em>Review IPS / IPS Addendum</em> - under your
            Trading case, with the same Subject and Due Date.
          </Callout>
        )}
      />

      <Trouble items={TROUBLE} />

      <Footnote>
        This guide describes the request form only. It is not investment advice, and it does not describe the
        suitability review of the trade.
      </Footnote>
    </GuidePage>
  );
}
